import { LanguageCode } from '@/interfaces';

export enum RouteName {
  Home = 'Home',
  ArticleSummary = 'ArticleSummary',
  Article = 'Article',
  SignInWithLinkedIn = 'SignInWithLinkedIn',
  Verify = 'Verify',
  TrackMyParcelEn = 'TrackMyParcelEn',
  TrackMyParcelMultiLanguages = 'TrackMyParcelMultiLanguages',
  AccountEn = 'Account',
  AccountMultiLanguages = 'AccountMultiLanguages',
  Carriers = 'Carriers',
  Regions = 'Regions',
  Blog = 'Blog',
  BlogPost = 'BlogPost',
}

interface IRoute {
  path: string;
  // biome-ignore lint/suspicious/noExplicitAny: route component type varies dynamically
  component: any;
}

const ROUTES: Record<string, IRoute> = {
  [RouteName.Home]: {
    path: '/',
    component: async () => import('pages'),
  },
  [RouteName.ArticleSummary]: {
    path: '/[articleTypeSlug]',
    component: async () => import('pages/[articleTypeSlug]'),
  },
  [RouteName.Article]: {
    path: '/[articleTypeSlug]/[slug]',
    component: async () => import('pages/[articleTypeSlug]/[slug]'),
  },
  [RouteName.SignInWithLinkedIn]: {
    path: '/sign-in-with-linkedin',
    component: async () => import('pages/sign-in-with-linkedin'),
  },
  [RouteName.Verify]: {
    path: '/verify',
    component: async () => import('pages/verify'),
  },
  [RouteName.TrackMyParcelEn]: {
    path: '/track-my-parcel',
    component: async () => import('pages/track-my-parcel'),
  },
  [RouteName.TrackMyParcelMultiLanguages]: {
    path: '/[articleTypeSlug]/track-my-parcel',
    component: async () => import('pages/[articleTypeSlug]/track-my-parcel'),
  },
  [RouteName.AccountEn]: {
    path: '/account',
    component: async () => import('pages/account'),
  },
  [RouteName.AccountMultiLanguages]: {
    path: '/[articleTypeSlug]/account',
    component: async () => import('pages/[articleTypeSlug]/account'),
  },
  [RouteName.Carriers]: {
    path: '/carrier',
    component: async () => import('pages/carrier'),
  },
  [RouteName.Regions]: {
    path: '/region',
    component: async () => import('pages/region'),
  },
  // The `/blog` namespace still holds 677 legacy `/blog/<slug>` → `/insights/...` redirects
  // (redirect-urls.json). Those run before page routing, so a new post must not reuse one of
  // those slugs — it would 301 away instead of rendering.
  [RouteName.Blog]: {
    path: '/blog',
    component: async () => import('pages/blog'),
  },
  [RouteName.BlogPost]: {
    path: '/blog/[slug]',
    component: async () => import('pages/blog/[slug]'),
  },
};

export enum AmpPageType {
  country = 'country',
  carrier = 'carrier',
}

export enum SeoPageType {
  global = 'global',
  region = 'region',
  country = 'country',
  carrier = 'carrier',
}

// Contentful `searchEngineIndexing` on landingPage.
//
// Indexing is OPT-IN: only an explicit `index` lists the page in main-sitemap.xml, so a newly
// created page starts out unadvertised instead of adding to the thin-content surface. Unset
// and `noindex` behave identically — this is deliberately the reverse of a "safe default",
// because the risk being managed is an uncurated page reaching Google, not the other way round.
//
// The robots meta tag follows this field on SEO pages (/<locale>/track-*) via
// LandingPageSEOHead; other landing pages take their robots tag from the separate
// `seo.robots` field via DocumentHead, so on those this field is sitemap-only.
export enum SearchEngineIndexing {
  index = 'index',
  noindex = 'noindex',
}

export const SUPPORTED_LOCALES = [
  'zh',
  'nl',
  'en',
  'fr',
  'de',
  'el',
  'hi',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'ms',
  'pl',
  'pt',
  'ro',
  'ru',
  'es',
  'ta',
  'th',
  'tr',
  'vi',
];

const LANGUAGE_CODES_GROUP = Object.values(LanguageCode);

export const MULTI_LANGUAGE_PAGE_SLUGS = ['track-my-parcel', 'account', 'manage-email-subscriptions'];

export const MULTI_LANGUAGE_PAGES_REGEX = new RegExp(
  `^/((${LANGUAGE_CODES_GROUP.join('|')})/)?(${MULTI_LANGUAGE_PAGE_SLUGS.join('|')})$`,
);

export default ROUTES;
